import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Chip from "core/components/Chip";
import { LegendChip } from "core/components/LegendChip";
import { useMemo } from "react";
import { DateTime, Interval } from "luxon";
import Typography from "core/components/Typography";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FieldError } from "core/components/Form/styles";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import {
  SettingLabel,
  ChipContainer,
  QueryStructure,
  QueryContainer,
  QueryRow,
  QueryItemContainer,
} from "./styles";
import { faCircleCheck, faCircleX } from "@fortawesome/pro-light-svg-icons";
import {
  SETTINGS,
  MAX_CHIPS,
  MAX_CHIPS_ADDED,
  MAX_CHIPS_REMOVED,
  API_DATE_FORMAT,
  LEGEND_VARIANTS,
  REVIEW_FIELD_MAPPING,
  PRODUCT_BUNDLES,
} from "./constants";
import { QueryItems } from "./DeploymentReview/UtilityComponents";
import Pill from "core/components/Pill";

// productLicense/utilities.js
export const createStateOptionsFromCatalogEntry = (
  catalogEntry,
  dataTableKey
) => {
  const stateFieldId = catalogEntry.data[dataTableKey].state_attribute;

  const stateFields = Object.values(
    catalogEntry.data[dataTableKey].fields
  ).find((field) => {
    return field.id === stateFieldId;
  });

  return stateFields.values.map((state) => {
    return {
      label: state,
      value: state,
    };
  });
};

export const areObjectsDeeplyEqual = (prevState, nextState) => {
  // Check types
  const typesMatch = Object.is(typeof prevState, typeof nextState);
  if (!typesMatch) return false;

  // Check if objects or arrays
  const isPrevObject =
    prevState && !Array.isArray(prevState) && typeof prevState === "object";
  const isNextObject =
    nextState && !Array.isArray(nextState) && typeof nextState === "object";
  const isPrevArray = prevState && Array.isArray(prevState);
  const isNextArray = nextState && Array.isArray(nextState);
  const areObjects = isPrevObject && isNextObject;
  const areArrays = isPrevArray && isNextArray;

  // Check if type object but mismatch on structure
  if ([isPrevObject && isNextArray, isPrevArray && isNextObject].includes(true))
    return false;

  // Check primitives
  if (!areObjects && !areArrays) {
    return Object.is(prevState, nextState);
  }

  // If objects or arrays check length of keys/items
  const prevKeys = Object.keys(prevState);
  const nextKeys = Object.keys(nextState);
  if (!Object.is(prevKeys.length, nextKeys.length)) return false;

  // If objects, check all keys have the same name
  if (areObjects) {
    const objectsHaveSameKeys = prevKeys.every((prevKey) =>
      Object.hasOwn(nextState, prevKey)
    );
    if (!objectsHaveSameKeys) return false;
  }

  // Finally recursively compare nested quality
  return prevKeys.every((prevKey) =>
    areObjectsDeeplyEqual(prevState[prevKey], nextState[prevKey])
  );
};

export const getComplexFieldValue = ({ dataId, data }) => {
  if (!dataId.includes(".")) {
    return data[dataId] ?? "";
  } else {
    const splitFieldKey = dataId.split(".");
    const nextKey = splitFieldKey.shift();

    return getComplexFieldValue({
      dataId: splitFieldKey.join("."),
      data: data[nextKey] ?? "",
    });
  }
};

export const appendLabelsToQueryItem = (obj, selected) => {
  const newObj = { ...obj };

  if (newObj.field && selected[newObj.field]) {
    newObj.label = selected[newObj.field];
  }

  if (Array.isArray(newObj.filters)) {
    newObj.filters = newObj.filters.map((filterObj) =>
      appendLabelsToQueryItem(filterObj, selected)
    );
  }

  return newObj;
};

export const extractSelectedItems = (item, catalogFields, selected) => {
  if (item.field) {
    const selectedItem = catalogFields.find((cf) => cf.value === item.field);

    if (selectedItem) {
      selected[selectedItem.value] = selectedItem.label;
    }
  }

  if (item.filters && Array.isArray(item.filters)) {
    item.filters.forEach((subItem) =>
      extractSelectedItems(subItem, catalogFields, selected)
    );
  }

  return selected;
};

export const extractFieldAndAppendLabels = (value, catalogFields) => {
  let selected = {};

  for (let i = 0; i < value.length; i++) {
    extractSelectedItems(value[i], catalogFields, selected);
  }

  return value.map((item) => appendLabelsToQueryItem(item, selected));
};

export const assignIntentRecursively = (item, intentValue) => {
  if (!item) return item;

  // Case 1: Simple query --> assign intent
  if (item.field) {
    return { ...item, intent: intentValue };
  }

  // Case 2: Query with filters --> recurse on each filter
  if (Array.isArray(item.filters)) {
    return {
      ...item,
      filters: item.filters.map((f) => assignIntentRecursively(f, intentValue)),
    };
  }

  return item;
};

export const compareAndAddIntentFlags = (
  currentItems = {},
  previousItems = {}
) => {
  const finalValues = [];

  let currentLength = Object.keys(currentItems).length;
  let previousLength = Object.keys(previousItems).length;

  for (let i = 0; i < Math.max(currentLength, previousLength); i++) {
    const current = currentItems[i];
    const prev = previousItems[i];

    // CASE 1: Simple query added/removed/modified/unmodified
    if (prev && prev.field) {
      if (current && current.field && current.id === prev.id) {
        if (areObjectsDeeplyEqual(prev, current)) {
          finalValues.push({ ...current, intent: LEGEND_VARIANTS.UNMODIFIED });
        } else {
          finalValues.push({ ...current, intent: LEGEND_VARIANTS.MODIFIED });
        }
      } else if (!current || current.id !== prev.id) {
        // previous exists but not current
        finalValues.push({ ...prev, intent: LEGEND_VARIANTS.NEGATIVE });
      }
      // Current object exists but does not match prev.id -> positive
      if (current && (!prev || current.id !== prev.id)) {
        finalValues.push({ ...current, intent: LEGEND_VARIANTS.POSITIVE });
      }
      continue;
    }

    // CASE 2: Query with filters added/removed/modified/unmodified
    if (prev && prev.filters) {
      const newObj = { ...prev, filters: [] };

      const prevMap = new Map(prev.filters.map((f) => [f.id, f]));
      const currentMap = new Map(
        (current?.filters || []).map((f) => [f.id, f])
      );

      // Check previous filters -> unmodified/modified/negative
      for (const [id, prevFilter] of prevMap.entries()) {
        if (currentMap.has(id)) {
          const currentFilter = currentMap.get(id);
          if (areObjectsDeeplyEqual(prevFilter, currentFilter)) {
            newObj.filters.push(
              assignIntentRecursively(currentFilter, LEGEND_VARIANTS.UNMODIFIED)
            );
          } else {
            newObj.filters.push(
              assignIntentRecursively(currentFilter, LEGEND_VARIANTS.MODIFIED)
            );
          }
        } else {
          newObj.filters.push(
            assignIntentRecursively(prevFilter, LEGEND_VARIANTS.NEGATIVE)
          );
        }
      }

      // Check for new filters -> positive
      for (const [id, currentFilter] of currentMap.entries()) {
        if (!prevMap.has(id)) {
          newObj.filters.push(
            assignIntentRecursively(currentFilter, LEGEND_VARIANTS.POSITIVE)
          );
        }
      }

      finalValues.push(newObj);
      continue;
    }

    // CASE 3: New query added
    if (current) {
      const copy = structuredClone(current); // structuredClone recursively copies all nested objects and arrays & can support types than JSON
      finalValues.push(assignIntentRecursively(copy, LEGEND_VARIANTS.POSITIVE));
    }

    // CASE 4: Existing query removed
    if (prev) {
      const copy = structuredClone(prev);
      finalValues.push(assignIntentRecursively(copy, LEGEND_VARIANTS.NEGATIVE));
    }
  }

  return finalValues;
};

export const COMPONENTS = {
  [SETTINGS.ANONYMIZE_DATA]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    return (
      <SettingLabel>
        Anonymize Data <b>{value ? "On" : "Off"}</b>
      </SettingLabel>
    );
  },
  [SETTINGS.ATTRIBUTES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    previousData,
    showNewChanges,
    extractedFields,
  }) => {
    const [limitKey, selectedKey] = reviewFieldKey.split(",");
    const productSelectedField = product.fields[selectedKey];

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === selectedKey))
        .controls.find((c) => c.fieldKey === selectedKey);
    }, [product.page.settings, selectedKey]);

    const selectedValue = getComplexFieldValue({
      dataId: productSelectedField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    let selected = [];
    let selectedItem;

    for (let i = 0; i < selectedValue.length; i++) {
      selectedItem = control.dynamicProps.catalogFields
        .find((item) =>
          item.children.find((it) => it.value === selectedValue[i])
        )
        ?.children.find((child) => child.value === selectedValue[i]).label;

      selected.push(selectedItem);
    }

    const showSelectedAttributes = showNewChanges
      ? true
      : extractedFields?.includes("licensed_field_group_ids");

    const hasMore = selectedValue?.length > MAX_CHIPS;
    // Validation runs against the draft, so the message belongs only on the
    // draft side. The left column renders previousData — server state the user
    // cannot edit — where an error would be meaningless.
    const error = showNewChanges
      ? [product.fieldErrors[limitKey], product.fieldErrors[selectedKey]]
          .join(" ")
          .trim()
      : "";

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  fontSize: 16,
                }}
              >
                <b>{selectedValue?.length.toLocaleString() || 0}</b> Selected
                Attributes /{" "}
                <b>
                  {(showNewChanges
                    ? product?.data?.license?.identified_consumer_attribute_limit?.toLocaleString()
                    : previousData?.license?.identified_consumer_attribute_limit?.toLocaleString()) ||
                    0}
                </b>{" "}
                Purchased Attributes
              </div>
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>
        {showSelectedAttributes && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#000000" }}>
              Selected Attributes
            </div>
            {selected?.length === 0 && (
              <span style={{ color: "#000000" }}>None</span>
            )}
            {selected && (
              <ChipContainer style={{ margin: 0 }}>
                {selected.slice(0, MAX_CHIPS).map((attribute) => {
                  return <Chip key={attribute} text={attribute} />;
                })}
                {hasMore && (
                  <span onClick={() => showMoreItems("Attributes", selected)}>
                    <Chip
                      text="See All"
                      variant="outlined"
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                )}
              </ChipContainer>
            )}
          </>
        )}
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginTop: "-15px",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.CONSUMER_RECORDS]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    return (
      <SettingLabel>
        <b>{Number(value).toLocaleString()}</b> Consumer Records
      </SettingLabel>
    );
  },
  [SETTINGS.RECORDS]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    return (
      <SettingLabel>
        <b>{Number(value).toLocaleString()}</b> Records Limit
      </SettingLabel>
    );
  },
  [SETTINGS.STATES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const hasMore = value?.length > MAX_CHIPS;

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === reviewFieldKey))
        .controls.find((c) => c.fieldKey === reviewFieldKey);
    }, [product.page.settings, reviewFieldKey]);

    const maxOptionsCount = control.dynamicProps.catalogFields.length;
    const showLengthOrAll = (selectedCount, maxCount) =>
      selectedCount === maxCount ? "All" : selectedCount;
    const isStateFilteringRequired = product.isStateFilteringRequired;
    const error = showNewChanges ? product.fieldErrors[reviewFieldKey] : "";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: typeof value !== "string" && value?.length > 0 ? 8 : 4,
        }}
      >
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div>
              {isStateFilteringRequired ? (
                <b>
                  {typeof value !== "string" && value?.length > 0
                    ? showLengthOrAll(value?.length, maxOptionsCount)
                    : 0}
                </b>
              ) : (
                <b>
                  {typeof value === "string" || !value || value?.length === 0
                    ? "All"
                    : value?.length}
                </b>
              )}{" "}
              States Selected
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>

        {typeof value !== "string" && value?.length > 0 ? (
          <ChipContainer style={{ margin: 0, padding: 0 }}>
            {value.slice(0, MAX_CHIPS).map((item) => {
              return <Chip text={item} key={item} />;
            })}
            {hasMore && (
              <span onClick={() => showMoreItems("States", value)}>
                <Chip
                  text="See All"
                  variant="outlined"
                  style={{ cursor: "pointer" }}
                />
              </span>
            )}
          </ChipContainer>
        ) : null}

        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.ZIPCODES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const hasMore = value?.length > MAX_CHIPS;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: value?.length > 0 ? 8 : 4,
        }}
      >
        <SettingLabel>
          <b>{value?.length || 0}</b> Selected Zip Codes
        </SettingLabel>
        {value?.length ? (
          <ChipContainer style={{ margin: 0, padding: 0 }}>
            {value.slice(0, MAX_CHIPS).map((item) => {
              return <Chip text={item} key={item} />;
            })}
            {hasMore && (
              <span
                onClick={() =>
                  showMoreItems(
                    "Zip codes",
                    value.map((zip) => ({ label: zip }))
                  )
                }
              >
                <Chip
                  text="See All"
                  variant="outlined"
                  style={{ cursor: "pointer" }}
                />
              </span>
            )}
          </ChipContainer>
        ) : null}
      </div>
    );
  },
  [SETTINGS.MODELS]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    previousData,
    showNewChanges,
    extractedFields,
  }) => {
    const [limitKey, selectedKey] = reviewFieldKey.split(",");
    const productLimitField = product.fields[limitKey];
    const productSelectedField = product.fields[selectedKey];
    const showSelectedAttributes = showNewChanges
      ? true
      : extractedFields?.includes("selected_models");

    const limitValue = getComplexFieldValue({
      dataId: productLimitField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const selectedValue = getComplexFieldValue({
      dataId: productSelectedField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === selectedKey))
        .controls.find((c) => c.fieldKey === selectedKey);
    }, [product.page.settings, selectedKey]);

    let selected = [];
    let selectedItem;

    for (let i = 0; i < selectedValue.length; i++) {
      selectedItem = control.dynamicProps.catalogFields.find(
        (item) => item.value === selectedValue[i]
      )?.label;

      selected.push(selectedItem);
    }
    const hasMore = selectedValue?.length > MAX_CHIPS;
    // The limit and the selection validate against each other, so either
    // field's message belongs on this one row. Both keys can hold a message at
    // once, but they describe the same conflict — show one, not a run-on pair.
    const error =
      (showNewChanges &&
        [product.fieldErrors[limitKey], product.fieldErrors[selectedKey]].find(
          Boolean
        )) ||
      "";

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div style={{ fontSize: 16 }}>
              <b>{selectedValue?.length || 0}</b> Selected Models /{" "}
              <b>{Number(limitValue).toLocaleString()}</b> Purchased Models
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>
        {showSelectedAttributes && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#000000" }}>
              Selected Attributes
            </div>
            {selected?.length === 0 && (
              <span style={{ color: "#000000" }}>None</span>
            )}

            {selected && (
              <ChipContainer style={{ margin: 0, padding: 0 }}>
                {selected.slice(0, MAX_CHIPS).map((item) => {
                  return <Chip text={item} key={item} />;
                })}
                {hasMore && (
                  <span onClick={() => showMoreItems("Models", selected)}>
                    <Chip
                      text="See All"
                      variant="outlined"
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                )}
              </ChipContainer>
            )}
          </>
        )}
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginTop: "-15px",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.QUARTERS]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const error = showNewChanges ? product.fieldErrors[reviewFieldKey] : "";

    return (
      <>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div>
              {value && typeof value !== "string" ? (
                <div
                  style={{
                    gap: 4,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      height: 15,
                    }}
                  >
                    Licensed Dates (Quarterly)
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {DateTime.fromFormat(value.from, API_DATE_FORMAT).toFormat(
                      "MMM-yyyy"
                    )}

                    <div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        size="sm"
                        style={{ marginBottom: "-1px" }}
                      />
                    </div>
                    {DateTime.fromFormat(value.to, API_DATE_FORMAT)
                      .plus({ months: 2 }) // to display the ending month of the quarter
                      .toFormat("MMM-yyyy")}
                  </div>
                </div>
              ) : (
                "No quarters selected"
              )}
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </>
    );
  },
  [SETTINGS.MONTHS]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const error = showNewChanges ? product.fieldErrors[reviewFieldKey] : "";

    return (
      <>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div>
              {value && typeof value !== "string" ? (
                <div
                  style={{
                    gap: 4,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      height: 15,
                    }}
                  >
                    Licensed Dates (Monthly)
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {DateTime.fromFormat(value.from, API_DATE_FORMAT).toFormat(
                      "MM/yyyy"
                    )}

                    <div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        size="sm"
                        style={{ marginBottom: "-1px" }}
                      />
                    </div>
                    {DateTime.fromFormat(value.to, API_DATE_FORMAT).toFormat(
                      "MM/yyyy"
                    )}
                  </div>
                </div>
              ) : (
                "No months selected"
              )}
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </>
    );
  },
  [SETTINGS.YEARS]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });
    const error = showNewChanges ? product.fieldErrors[reviewFieldKey] : "";

    return (
      <>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div>
              {value && typeof value !== "string" ? (
                <div
                  style={{
                    gap: 4,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      height: 15,
                    }}
                  >
                    Licensed Dates (Yearly)
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {DateTime.fromFormat(value.from, API_DATE_FORMAT).toFormat(
                      "yyyy"
                    )}

                    <div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        size="sm"
                        style={{ marginBottom: "-1px" }}
                      />
                    </div>
                    {DateTime.fromFormat(value.to, API_DATE_FORMAT).toFormat(
                      "yyyy"
                    )}
                  </div>
                </div>
              ) : (
                "No years selected"
              )}
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </>
    );
  },
  [SETTINGS.QUERY]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const catalogFields = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === reviewFieldKey))
        .controls.find((c) => c.fieldKey === reviewFieldKey).dynamicProps
        .catalogFields;
    }, [product.page.settings, reviewFieldKey]);

    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    const valuesWithLabels = extractFieldAndAppendLabels(value, catalogFields);
    const updatedValue = valuesWithLabels.map((item) =>
      assignIntentRecursively(item, LEGEND_VARIANTS.UNMODIFIED)
    );

    return (
      <>
        <SettingLabel>Filters</SettingLabel>
        {typeof updatedValue !== "string" && updatedValue?.length > 0 ? (
          <QueryStructure>
            <QueryContainer className="queryContainer">
              <QueryRow>
                <Pill
                  text="And"
                  size="small"
                  variant="operator"
                  style={{ display: "inline" }}
                />
              </QueryRow>
              <QueryItemContainer className="queryItemContainer">
                <QueryItems
                  items={updatedValue}
                  showMoreItems={showMoreItems}
                />
              </QueryItemContainer>
            </QueryContainer>
          </QueryStructure>
        ) : (
          <Typography variant="p" noMargin color="#000000">
            No filters defined.
          </Typography>
        )}
      </>
    );
  },
  [SETTINGS.TOGGLE]: ({
    product,
    reviewFieldKey,
    previousData,
    showNewChanges,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: showNewChanges ? product.data : previousData,
    });

    return <SettingLabel>{value ? "" : "Not "} Licensed</SettingLabel>;
  },
};

export const UPDATED_COMPONENTS = {
  [SETTINGS.ATTRIBUTES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    updatedData,
  }) => {
    const [limitKey, selectedKey] = reviewFieldKey.split(",");

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === selectedKey))
        .controls.find((c) => c.fieldKey === selectedKey);
    }, [product.page.settings, selectedKey]);

    const productSelectedField = product.fields[selectedKey];
    const { addedItems, removedItems } = updatedData;

    const addedItemKeys = Object.keys(addedItems);
    let addedValues;
    let added = [];

    if (addedItemKeys.includes(productSelectedField.dataId)) {
      addedValues = addedItems[productSelectedField.dataId];
      let addedItem;
      for (let i = 0; i < addedValues.length; i++) {
        addedItem = control.dynamicProps.catalogFields
          .find((item) =>
            item.children.find((it) => it.value === addedValues[i])
          )
          .children.find((child) => child.value === addedValues[i]).label;

        added.push(addedItem);
      }
    }

    const removedItemKeys = Object.keys(removedItems);
    let removedValues;
    let removed = [];
    if (removedItemKeys.includes(productSelectedField.dataId)) {
      removedValues = removedItems[productSelectedField.dataId];
      let removedItem;
      for (let i = 0; i < removedValues.length; i++) {
        removedItem = control.dynamicProps.catalogFields
          .find((item) =>
            item.children.find((it) => it.value === removedValues[i])
          )
          .children.find((child) => child.value === removedValues[i]).label;

        removed.push(removedItem);
      }
    }

    const hasMoreAdded = added?.length > MAX_CHIPS_ADDED;
    const hasMoreRemoved = removed?.length > MAX_CHIPS_REMOVED;

    const selectedValue = getComplexFieldValue({
      dataId: productSelectedField.dataId,
      data: product.data,
    });

    const error = [
      product.fieldErrors[limitKey],
      product.fieldErrors[selectedKey],
    ]
      .join(" ")
      .trim();

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  fontSize: 16,
                }}
              >
                <b>{selectedValue?.length.toLocaleString() || 0}</b> Selected
                Attributes /{" "}
                <b>
                  {product?.data?.license?.identified_consumer_attribute_limit?.toLocaleString() ||
                    0}
                </b>{" "}
                Purchased Attributes
              </div>
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>

        {(added?.length > 0 || removed?.length > 0) && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#000000" }}>
              Selected Attributes
            </div>
            <ChipContainer
              style={{
                display: added?.length === 0 && "none",
                margin: 0,
                padding: 0,
              }}
            >
              {added?.slice(0, MAX_CHIPS_ADDED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleCheck}
                    intent={LEGEND_VARIANTS.POSITIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreAdded && (
                <span onClick={() => showMoreItems("Attributes Added", added)}>
                  <Chip
                    text="See All Added"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
            <ChipContainer
              style={{
                display: removed?.length === 0 && "none",
                gap: 5,
                margin: 0,
                padding: 0,
              }}
            >
              {removed?.slice(0, MAX_CHIPS_REMOVED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleX}
                    intent={LEGEND_VARIANTS.NEGATIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreRemoved && (
                <span
                  onClick={() => showMoreItems("Attributes Removed", removed)}
                >
                  <Chip
                    text="See All Removed"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
          </>
        )}
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginTop: "-9px",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.STATES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    updatedData,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const { addedItems, removedItems } = updatedData;

    const addedItemKeys = Object.keys(addedItems);
    let addedValues;
    if (addedItemKeys.includes(productField.dataId)) {
      addedValues = addedItems[productField.dataId];
    }

    const removedItemKeys = Object.keys(removedItems);
    let removedValues;
    if (removedItemKeys.includes(productField.dataId)) {
      removedValues = removedItems[productField.dataId];
    }

    const hasMoreAdded = addedValues?.length > MAX_CHIPS_ADDED;
    const hasMoreRemoved = removedValues?.length > MAX_CHIPS_REMOVED;

    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: product.data,
    });

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === reviewFieldKey))
        .controls.find((c) => c.fieldKey === reviewFieldKey);
    }, [product.page.settings, reviewFieldKey]);

    const maxOptionsCount = control.dynamicProps.catalogFields.length;
    const showLengthOrAll = (selectedCount, maxCount) =>
      selectedCount === maxCount ? "All" : selectedCount;
    const isStateFilteringRequired = product.isStateFilteringRequired;
    const error = product.fieldErrors[reviewFieldKey];

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: addedValues ? 8 : 4,
        }}
      >
        <SettingLabel style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div>
              {isStateFilteringRequired ? (
                <b>
                  {typeof value !== "string" && value?.length > 0
                    ? showLengthOrAll(value?.length, maxOptionsCount)
                    : 0}
                </b>
              ) : (
                <b>
                  {typeof value === "string" || !value || value?.length === 0
                    ? "All"
                    : value?.length}
                </b>
              )}{" "}
              States Selected
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>

        {typeof value !== "string" && (addedValues || removedValues) ? (
          <>
            <ChipContainer
              style={{
                display: addedValues?.length === 0 && "none",
                margin: 0,
                padding: 0,
              }}
            >
              {addedValues?.slice(0, MAX_CHIPS_ADDED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleCheck}
                    intent={LEGEND_VARIANTS.POSITIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreAdded && (
                <span
                  onClick={() => showMoreItems("States Added", addedValues)}
                >
                  <Chip
                    text="See All Added"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
            <ChipContainer
              style={{
                gap: 5,
                display:
                  (removedValues?.length === 0 || addedValues?.length === 0) &&
                  "none",
                margin: 0,
                padding: 0,
              }}
            >
              {removedValues?.slice(0, MAX_CHIPS_REMOVED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleX}
                    intent={LEGEND_VARIANTS.NEGATIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreRemoved && (
                <span
                  onClick={() => showMoreItems("States Removed", removedValues)}
                >
                  <Chip
                    text="See All Removed"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
          </>
        ) : null}

        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.ZIPCODES]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    updatedData,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const { addedItems, removedItems } = updatedData;

    const addedItemKeys = Object.keys(addedItems);
    let addedValues;
    if (addedItemKeys.includes(productField.dataId)) {
      addedValues = addedItems[productField.dataId];
    }

    const removedItemKeys = Object.keys(removedItems);
    let removedValues;
    if (removedItemKeys.includes(productField.dataId)) {
      removedValues = removedItems[productField.dataId];
    }

    const hasMoreAdded = addedValues?.length > MAX_CHIPS_ADDED;
    const hasMoreRemoved = removedValues?.length > MAX_CHIPS_REMOVED;

    const value = getComplexFieldValue({
      dataId: productField.dataId,
      data: product.data,
    });

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: addedValues ? 8 : 4,
        }}
      >
        <SettingLabel>
          <b>{value?.length || 0}</b> Selected Zip Codes
        </SettingLabel>
        {value?.length && (addedValues || removedValues) ? (
          <>
            <ChipContainer
              style={{
                display: addedValues?.length === 0 && "none",
                margin: 0,
                padding: 0,
              }}
            >
              {addedValues?.slice(0, MAX_CHIPS_ADDED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleCheck}
                    intent={LEGEND_VARIANTS.POSITIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreAdded && (
                <span
                  onClick={() =>
                    showMoreItems(
                      "Zip codes Added",
                      addedValues.map((zip) => ({ label: zip }))
                    )
                  }
                >
                  <Chip
                    text="See All Added"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
            <ChipContainer
              style={{
                gap: 5,
                paddingBottom: 7,
                display: removedValues?.length === 0 && "none",
                margin: 0,
                padding: 0,
              }}
            >
              {removedValues?.slice(0, MAX_CHIPS_REMOVED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleX}
                    intent={LEGEND_VARIANTS.NEGATIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreRemoved && (
                <span
                  onClick={() =>
                    showMoreItems(
                      "Zip codes Removed",
                      removedValues.map((zip) => ({ label: zip }))
                    )
                  }
                >
                  <Chip
                    text="See All Removed"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
          </>
        ) : null}
      </div>
    );
  },
  [SETTINGS.MODELS]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    updatedData,
  }) => {
    const [limitKey, selectedKey] = reviewFieldKey.split(",");
    const productLimitField = product.fields[limitKey];
    const productSelectedField = product.fields[selectedKey];

    const limitValue = getComplexFieldValue({
      dataId: productLimitField.dataId,
      data: product.data,
    });
    const selectedValue = getComplexFieldValue({
      dataId: productSelectedField.dataId,
      data: product.data,
    });

    const control = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === selectedKey))
        .controls.find((c) => c.fieldKey === selectedKey);
    }, [product.page.settings, selectedKey]);

    const { addedItems, removedItems } = updatedData;

    const addedItemKeys = Object.keys(addedItems);
    let addedValues;
    let added = [];

    if (addedItemKeys.includes(productSelectedField.dataId)) {
      addedValues = addedItems[productSelectedField.dataId];
      let addedItem;
      for (let i = 0; i < addedValues.length; i++) {
        addedItem = control.dynamicProps.catalogFields.find(
          (item) => item.value === addedValues[i]
        ).label;

        added.push(addedItem);
      }
    }

    const removedItemKeys = Object.keys(removedItems);
    let removedValues;
    let removed = [];
    if (removedItemKeys.includes(productSelectedField.dataId)) {
      removedValues = removedItems[productSelectedField.dataId];
      let removedItem;
      for (let i = 0; i < removedValues.length; i++) {
        removedItem = control.dynamicProps.catalogFields.find(
          (item) => item.value === removedValues[i]
        ).label;

        removed.push(removedItem);
      }
    }

    const hasMoreAdded = added?.length > MAX_CHIPS_ADDED;
    const hasMoreRemoved = removed?.length > MAX_CHIPS_REMOVED;
    // The limit and the selection validate against each other, so either
    // field's message belongs on this one row. Both keys can hold a message at
    // once, but they describe the same conflict — show one, not a run-on pair.
    const error =
      [product.fieldErrors[limitKey], product.fieldErrors[selectedKey]].find(
        Boolean
      ) || "";

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SettingLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div style={{ fontSize: 16 }}>
              <b>{selectedValue?.length || 0}</b> Selected Models /{" "}
              <b>{Number(limitValue).toLocaleString()}</b> Purchased Models
            </div>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ marginBottom: "2px", color: "#BA351C" }}
              />
            )}
          </div>
        </SettingLabel>

        {(added?.length > 0 || removed?.length > 0) && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#000000" }}>
              Selected Attributes
            </div>
            <ChipContainer
              style={{
                display: added?.length === 0 && "none",
                margin: 0,
                padding: 0,
              }}
            >
              {added?.slice(0, MAX_CHIPS_ADDED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleCheck}
                    intent={LEGEND_VARIANTS.POSITIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreAdded && (
                <span onClick={() => showMoreItems("Models Added", added)}>
                  <Chip
                    text="See All Added"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
            <ChipContainer
              style={{
                margin: 0,
                padding: 0,
                display: removed?.length === 0 && "none",
              }}
            >
              {removed?.slice(0, MAX_CHIPS_REMOVED).map((attribute) => {
                return (
                  <LegendChip
                    key={attribute}
                    text={attribute}
                    rightIcon={faCircleX}
                    intent={LEGEND_VARIANTS.NEGATIVE}
                    style={{
                      height: "fit-content",
                      width: "fit-content",
                      padding: "3px 8px",
                      borderRadius: 4,
                    }}
                  />
                );
              })}
              {hasMoreRemoved && (
                <span onClick={() => showMoreItems("Models Removed", removed)}>
                  <Chip
                    text="See All Removed"
                    size="small"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                </span>
              )}
            </ChipContainer>
          </>
        )}
        {error && (
          <FieldError
            style={{
              textAlign: "left",
              marginTop: "-15px",
              marginBottom: "15px",
            }}
          >
            {error}
          </FieldError>
        )}
      </div>
    );
  },
  [SETTINGS.QUERY]: ({
    product,
    reviewFieldKey,
    showMoreItems,
    updatedData,
  }) => {
    const productField = product.fields[reviewFieldKey];
    const catalogFields = useMemo(() => {
      return product.page.settings
        .find((s) => s.controls.find((c) => c.fieldKey === reviewFieldKey))
        .controls.find((c) => c.fieldKey === reviewFieldKey).dynamicProps
        .catalogFields;
    }, [product.page.settings, reviewFieldKey]);

    const { addedItems: currentItems, removedItems: previousItems } =
      updatedData;

    const value = compareAndAddIntentFlags(
      currentItems[productField.dataId],
      previousItems[productField.dataId]
    );

    const valuesWithLabels = extractFieldAndAppendLabels(value, catalogFields);

    return (
      <>
        <SettingLabel>Filters</SettingLabel>
        {typeof valuesWithLabels !== "string" &&
        valuesWithLabels?.length > 0 ? (
          <QueryStructure>
            <QueryContainer className="queryContainer">
              <QueryRow>
                <Pill
                  text="And"
                  size="small"
                  variant="operator"
                  style={{ display: "inline" }}
                />
              </QueryRow>
              <QueryItemContainer className="queryItemContainer">
                <QueryItems
                  items={valuesWithLabels}
                  showMoreItems={showMoreItems}
                />
              </QueryItemContainer>
            </QueryContainer>
          </QueryStructure>
        ) : (
          <Typography variant="p" noMargin color="#000000">
            No filters defined.
          </Typography>
        )}
      </>
    );
  },
};

export const deepMergeObjects = (
  prevUpdates = {},
  nextUpdates = {},
  id = "standard"
) => {
  if ([prevUpdates, nextUpdates].includes(null)) {
    return prevUpdates ?? nextUpdates;
  }

  const uniqueKeys = [
    ...new Set([...Object.keys(prevUpdates), ...Object.keys(nextUpdates)]),
  ];

  return uniqueKeys.reduce((mergedValues, key) => {
    const prevValue = prevUpdates[key];
    let nextValue = nextUpdates[key];

    if (key === "states" && nextValue === null) {
      nextValue = [];
    }

    if ([prevValue, nextValue].includes(undefined)) {
      return {
        ...mergedValues,
        [key]: nextValue ?? prevValue,
      };
    }

    const isPrevValueObject =
      prevValue && !Array.isArray(prevValue) && typeof prevValue === "object";
    const isNextValueObject =
      nextValue && !Array.isArray(nextValue) && typeof nextValue === "object";

    if (isPrevValueObject && isNextValueObject) {
      return {
        ...mergedValues,
        [key]: deepMergeObjects(prevValue, nextValue, "recursive"),
      };
    } else {
      return {
        ...mergedValues,
        [key]: nextValue,
      };
    }
  }, {});
};

export const compareObjects = (prevData, newData) => {
  const result = {
    addedItems: {},
    removedItems: {},
  };

  // Helper function to compare arrays
  const compareArrays = (prevArray, newArray) => {
    const added = newArray.filter((item) => !prevArray.includes(item));
    const removed = prevArray.filter((item) => !newArray.includes(item));
    return { added, removed };
  };

  // Recursive function to compare objects
  const compare = (prevObj, newObj, path = "") => {
    for (let key in prevObj) {
      const prevValue = prevObj[key];
      const newValue = newObj[key];

      if (!(key in newObj)) {
        // Key is removed in newData
        result.removedItems[path + key] = prevValue;
      } else if (Array.isArray(prevValue) && Array.isArray(newValue)) {
        // Compare arrays
        const { added, removed } = compareArrays(prevValue, newValue);
        if (added.length) {
          result.addedItems[path + key] = added;
        }
        if (removed.length) {
          result.removedItems[path + key] = removed;
        }
      } else if (
        typeof prevValue === "object" &&
        typeof newValue === "object" &&
        prevValue !== null &&
        newValue !== null
      ) {
        // Recurse for nested objects
        compare(prevValue, newValue, path + key + ".");
      } else if (prevValue !== newValue) {
        // Values are different
        result.removedItems[path + key] = prevValue;
        result.addedItems[path + key] = newValue;
      }
    }

    // Check for added keys in newData
    for (let key in newObj) {
      if (!(key in prevObj)) {
        const newValue = newObj[key];
        if (Array.isArray(newValue)) {
          result.addedItems[path + key] = newValue;
        } else {
          result.addedItems[path + key] = newValue;
        }
      }
    }
  };

  compare(prevData, newData);
  return result;
};

export const extractUpdatedFields = (fieldArray = [], productId) => {
  let fields = [];

  for (let i = 0; i < fieldArray.length; i++) {
    const field = fieldArray[i].split(".").reverse()[0];
    fields.push(field);
  }

  return {
    [productId]: fields,
  };
};

export const getReviewFieldMapping = (productId, fields) => {
  let mapping;
  let result = [];
  for (let i = 0; i < fields.length; i++) {
    mapping = REVIEW_FIELD_MAPPING[productId][fields[i]];
    result.push(mapping);
  }
  const uniqueResult = [...new Set([...result])];

  return {
    [productId]: uniqueResult,
  };
};

export const getFilteredReviewFields = (
  updatedData,
  productId,
  reviewFields
) => {
  const reviewFieldsUpdated = [
    ...new Set(
      Object.keys(updatedData?.addedItems).concat(
        Object.keys(updatedData?.removedItems)
      )
    ),
  ];

  const extractedFields = extractUpdatedFields(reviewFieldsUpdated, productId);
  const reviewFieldMapping = getReviewFieldMapping(
    productId,
    extractedFields[productId]
  );

  try {
    const filteredReviewFields = Object.fromEntries(
      Object.entries(reviewFields).filter(([key]) =>
        reviewFieldMapping[productId]?.includes(key)
      )
    );
    return { extractedFields, filteredReviewFields };
  } catch (error) {
    console.error(error);
    return { extractedFields: [], filteredReviewFields: {} };
  }
};

export const dateRangeControlFormatter = ({ fieldValue }) => {
  if (
    !fieldValue ||
    typeof fieldValue !== "object" ||
    Array.isArray(fieldValue)
  ) {
    return null;
  }

  if (Object.hasOwn(fieldValue, "from") && Object.hasOwn(fieldValue, "to")) {
    return Interval.fromDateTimes(
      DateTime.fromFormat(fieldValue.from, API_DATE_FORMAT),
      DateTime.fromFormat(fieldValue.to, API_DATE_FORMAT)
    );
  }

  return null;
};

export const dateRangeAPIFormatter = ({ fieldValue, isValid }) => {
  if (fieldValue && isValid) {
    const [start, end] = [
      fieldValue.start.toFormat(API_DATE_FORMAT),
      fieldValue.end.toFormat(API_DATE_FORMAT),
    ];

    return {
      operator: "between",
      from: start,
      to: end,
    };
  } else {
    return fieldValue;
  }
};

export const dateRangeValidator = ({ product, field }) => {
  const { fieldErrors } = product;
  const { fieldId, fieldValue } = field;

  if (!fieldValue) {
    return [
      true,
      {
        ...fieldErrors,
        [fieldId]: "",
      },
    ];
  }

  if (!fieldValue.isValid) {
    return [
      false,
      {
        ...fieldErrors,
        [fieldId]: "Invalid date range.",
      },
    ];
  }

  const [start, end] = [
    fieldValue.start.toFormat(API_DATE_FORMAT),
    fieldValue.end.toFormat(API_DATE_FORMAT),
  ];

  if (!start || !end) {
    return [
      false,
      {
        ...fieldErrors,
        [fieldId]: "Invalid date range.",
      },
    ];
  }

  return [
    true,
    {
      ...fieldErrors,
      [fieldId]: "",
    },
  ];
};

export const selectedStatesValidator = ({ product, field }) => {
  const { fieldErrors } = product;
  const { fieldId, fieldValue } = field;
  const isStateFilteringRequired = product.isStateFilteringRequired || false;
  const isDefined = fieldValue;
  const isArray = isDefined && Array.isArray(fieldValue);
  const hasItems = isArray && fieldValue.length;

  if (isStateFilteringRequired) {
    if (hasItems) {
      return [
        true,
        {
          ...fieldErrors,
          [fieldId]: "",
        },
      ];
    } else {
      return [
        false,
        {
          ...fieldErrors,
          [fieldId]: "Must select at least 1 state.",
        },
      ];
    }
  } else {
    if (isArray && !hasItems) {
      return [
        false,
        {
          ...fieldErrors,
          [fieldId]: "Must select at least 1 state.",
        },
      ];
    } else {
      return [
        true,
        {
          ...fieldErrors,
          [fieldId]: "",
        },
      ];
    }
  }
};

export const buildComplexFieldState = ({
  prevState = {},
  fieldKey,
  newValue,
}) => {
  if (!fieldKey.includes(".")) {
    return { ...prevState, [fieldKey]: newValue };
  } else {
    const splitFieldKey = fieldKey.split(".");
    const nextKey = splitFieldKey.shift();

    return {
      ...prevState,
      [nextKey]: splitFieldKey.length
        ? buildComplexFieldState({
            prevState: prevState[nextKey],
            fieldKey: splitFieldKey.join("."),
            newValue,
          })
        : newValue,
    };
  }
};

/**
 * Records one field change against the audit trail of pending product updates.
 *
 * `dataId` is a dotted path ("license.model_limit"), so a single change expands
 * to a nested object. Seeding `buildComplexFieldState` with the product's
 * existing updates is what preserves sibling leaves: building the partial from
 * `{}` and spreading it over the previous updates replaces the whole `license`
 * branch, so every field but the last one edited is dropped from the payload.
 */
export const applyUpdateToAuditTrail = ({
  auditTrail,
  productId,
  dataId,
  newValue,
}) => {
  const index = auditTrail.findIndex((item) => item.productId === productId);

  if (index === -1) {
    return [
      ...auditTrail,
      {
        productId,
        productUpdates: buildComplexFieldState({
          prevState: {},
          fieldKey: dataId,
          newValue,
        }),
      },
    ];
  }

  const updatedItem = {
    ...auditTrail[index],
    productUpdates: buildComplexFieldState({
      prevState: auditTrail[index].productUpdates,
      fieldKey: dataId,
      newValue,
    }),
  };

  return [
    ...auditTrail.slice(0, index),
    updatedItem,
    ...auditTrail.slice(index + 1),
  ];
};

export const areProductsEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  return keys1.every(
    (key) => JSON.stringify(obj1[key]) === JSON.stringify(obj2[key])
  );
};

export const getBundleOptions = () => {
  let obj = [];

  if (PRODUCT_BUNDLES && Object.keys(PRODUCT_BUNDLES).length > 0) {
    const productBundles = Object.keys(PRODUCT_BUNDLES);

    for (let i = 0; i < productBundles.length; i++) {
      const bundle = Object.values(PRODUCT_BUNDLES).find(
        (item) => item.id === productBundles[i]
      );

      const option = {
        value: "true",
        label: bundle?.name,
        id: bundle?.id,
        path: bundle?.path,
        isDisabled: false,
      };

      obj.push(option);
    }
  }

  return obj;
};
